'use client';

import { useEffect, useState } from "react";
import { normalizeColor } from "./utils";
import { MolstarWrapper } from "@loschmidt/molstar";
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { Asset } from 'molstar/lib/mol-util/assets';
import { Protein } from "./types";

export function useProtein(
  viewer: MolstarWrapper | undefined,
  protein: Protein | undefined,
) {

  const [proteinModel, setProteinModel] = useState<string>();

  // Render protein
  useEffect(() => {
    let cancelled = false;
    let dataRef: string;
    (async () => {
      if (viewer && protein) {
        console.debug("[MolstarViewer] Protein rendering...");
        const data = await viewer.plugin.builders.data.download({
          url: Asset.Url(protein.url),
          isBinary: protein.isBinary ?? false,
          label: protein.label ?? "LABEL",
        }, {
          state: { isGhost: true }
        });
        if (cancelled) {
          return;
        }
        dataRef = data.ref;
        const trajectory = await viewer.plugin.builders.structure.parseTrajectory(data, protein.format ?? "pdb",);
        const model = await viewer.plugin.builders.structure.createModel(trajectory);
        const structure = await viewer.plugin.builders.structure.createStructure(model);
        const polymer = await viewer.plugin.builders.structure.tryCreateComponentStatic(structure, 'protein');

        const update = viewer.plugin.state.data.build();
        const builder = viewer.plugin.builders.structure.representation;

        const color = protein.color ? {
          color: 'uniform' as const,
          colorParams: { value: normalizeColor(protein.color) },
        } : {
          color: 'chain-id' as const,
        }

        builder.buildRepresentation(
          update,
          polymer,
          {
            type: protein.representationType ?? 'cartoon',
            ...color,
          },
          // { tag: protein.label }
        )

        if (!cancelled) {
          await update.commit({ revertOnError: true });
        }

        setProteinModel(model.ref);
      }
    })();
    return () => {
      cancelled = true;
      setProteinModel(undefined);
      if (viewer) {
        if (dataRef) {
          PluginCommands.State.RemoveObject(viewer.plugin, {
            state: viewer.plugin.state.data,
            ref: dataRef,
          });
        }
      }
    }
  }, [viewer, protein]);

  return proteinModel;
}

